import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
} from "@apollo/client/core";
import { ErrorLink } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { CloseCode, createClient } from "graphql-ws";
import { cloneDeepWith } from "lodash";

import router from "@/router/index.ts";
import axios, {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  REFRESH_TOKEN_URL,
  TOKEN_TYPE,
} from "@/utils/axios.ts";

const TOKEN_LIFETIME = 1000 * 60 * 15;

let refreshTokenPromise: Promise<any> | null = null;

async function refreshAccessToken() {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = axios
    .post(REFRESH_TOKEN_URL, {
      refreshToken: localStorage.getItem(REFRESH_TOKEN),
    })
    .then(({ data }) => {
      if (data?.accessToken) {
        localStorage.setItem(ACCESS_TOKEN, data.accessToken);
        return data;
      }
      throw new Error("No access token received");
    })
    .catch((err) => {
      localStorage.removeItem(ACCESS_TOKEN);
      localStorage.removeItem(REFRESH_TOKEN);
      if (router.currentRoute.value.name !== "login") {
        router.push({ name: "login" });
      }
      throw err;
    })
    .finally(() => {
      refreshTokenPromise = null;
    });

  return refreshTokenPromise;
}

const errorMiddleware = new ErrorLink(({ graphQLErrors, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      switch (err?.extensions?.code) {
        case "UNAUTHENTICATED": {
          return new Observable((observer) => {
            refreshAccessToken()
              .then((response) => {
                if (!response?.accessToken) {
                  throw new Error("Authentication failed - please log in again");
                }
                const oldHeaders = operation.getContext().headers;

                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: `${TOKEN_TYPE} ${response.accessToken}`,
                  },
                });
              })
              .then(() => {
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                };

                forward(operation).subscribe(subscriber);
              })
              .catch((error) => {
                observer.error(error || new Error("Authentication failed"));
              });
          });
        }
      }
    }
  }
});

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/gql`
    : "/gql",
});

let shouldRefreshToken = false;

// the socket close timeout due to token expiry
let tokenExpiryTimeout: number;

// let attempts = 0

const wsLink = new GraphQLWsLink(
  createClient({
    url:
      import.meta.env.VITE_APP_SUBSCRIPTIONS_URL || "ws://localhost:3000/wsgql",
    retryAttempts: 10,
    connectionAckWaitTimeout: 5000,
    connectionParams: async () => {
      if (shouldRefreshToken) {
        // refresh the token because it is no longer valid
        await refreshAccessToken();

        // and reset the flag to avoid refreshing too many times
        shouldRefreshToken = false;
      }

      return { token: localStorage.getItem(ACCESS_TOKEN) };
    },
    on: {
      connected: (socket: any) => {
        clearTimeout(tokenExpiryTimeout);

        // set a token expiry timeout for closing the socket
        // with an `4403: Forbidden` close event indicating
        // that the token expired. the `closed` event listner below
        // will set the token refresh flag to true
        tokenExpiryTimeout = window.setTimeout(() => {
          if (socket.readyState === WebSocket.OPEN)
            socket.close(CloseCode.Forbidden, "Forbidden");
        }, TOKEN_LIFETIME);
      },
      closed: (event: any) => {
        // if closed with the `4403: Forbidden` close event
        // the client or the server is communicating that the token
        // is no longer valid and should be therefore refreshed
        if (event.code === CloseCode.Forbidden) shouldRefreshToken = true;
      },
    },
  }),
);

const link = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return (
      definition.kind === "OperationDefinition"
      && definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink,
);

const authMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: `${TOKEN_TYPE} ${localStorage.getItem(ACCESS_TOKEN)}`,
    },
  }));

  return forward(operation);
});

const removeTypename = new ApolloLink((operation, forward) => {
  const newOperation = operation;

  newOperation.variables = cloneDeepWith(
    newOperation?.variables ?? {},
    (value) => {
      if (value && value.__typename) {
        const { __typename, ...valWithoutTypename } = value;

        return valWithoutTypename;
      }

      return undefined;
    },
  );

  return forward(newOperation);
});

const cache = new InMemoryCache();

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([removeTypename, authMiddleware, errorMiddleware, link]),
  cache,
  defaultOptions: {
    query: {
      fetchPolicy: "network-only",
    },
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
  devtools: {
    enabled: import.meta.env.DEV,
  },
});

export const apollo = apolloClient;
